import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Building2, Loader2, PlayCircle, Sparkles, Plus, Users, Copy, Crown, Upload, Trash2, UserCircle2, Mail, Palette, Globe, Eye } from "lucide-react";

/**
 * SuperAdminPlatform — the screen super_admin sees at /admin instead of the
 * tenant-bound SchoolAdmin view. Shows all flying schools, lets you create
 * new ones, and exposes the global Avatar Studio for lesson content.
 */
export default function SuperAdminPlatform() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [avStatus, setAvStatus] = useState({});
  const [starting, setStarting] = useState(null);
  const [newSchool, setNewSchool] = useState({ name: "", icao_code: "", admin_email: "", website_url: "" });
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewPalette, setPreviewPalette] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [instructorsBySchool, setInstructorsBySchool] = useState({});
  const [openSchool, setOpenSchool] = useState(null); // school object whose instructor manager is open
  const [openSchoolStudio, setOpenSchoolStudio] = useState(null);
  const [openSchoolTheme, setOpenSchoolTheme] = useState(null);
  const fileInputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/schools"),
        api.get("/lessons"),
      ]);
      setSchools(s.data);
      setLessons(l.data);
      // Avatar Studio status per lesson (one quick parallel fetch)
      const statuses = await Promise.all(l.data.map((ll) =>
        api.get(`/admin/lessons/${ll.id}/avatar-studio/status`).then(r => [ll.id, r.data]).catch(() => [ll.id, null])
      ));
      const map = {};
      statuses.forEach(([id, st]) => { if (st) map[id] = st; });
      setAvStatus(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const createSchool = async () => {
    if (!newSchool.name.trim()) return toast.error("Name required");
    setCreating(true);
    try {
      const { data: school } = await api.post("/schools", newSchool);
      if (previewPalette) {
        await api.patch(`/admin/schools/${school.school_id}`, { theme_palette: previewPalette });
      }
      toast.success(newSchool.admin_email ? `School created. ${newSchool.admin_email} will be school_admin on next sign-in.` : "School created");
      setNewSchool({ name: "", icao_code: "", admin_email: "", website_url: "" });
      setPreviewPalette(null);
      setCreateOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const suggestTheme = async (websiteUrl, schoolId) => {
    if (!websiteUrl?.trim()) return toast.error("Enter a website URL first");
    setSuggesting(true);
    try {
      const targetId = schoolId || schools[0]?.school_id;
      if (!targetId) {
        toast.message("Create the school first, then suggest a theme from its card");
        return;
      }
      const { data } = await api.post(`/admin/schools/${targetId}/theme/suggest`, { website_url: websiteUrl });
      // Build a richer preview that carries the brand profile too
      const palette = { primary: data.primary, accent: data.accent, dark: data.dark, light: data.light };
      const profile = {
        hero_image_url: data.hero_image_url,
        heading_font: data.heading_font,
        body_font: data.body_font,
        font_links: data.font_links || [],
        vibe: data.vibe,
        tagline: data.tagline,
      };
      setPreviewPalette({ ...palette, _profile: profile });
      if (data.blocked_by_bot_wall) {
        toast.warning("That site has bot protection — colors might be off. Try uploading a screenshot or logo image instead.", { duration: 6000 });
      } else {
        toast.success(`Suggested ${data.vibe} brand · ${data.heading_font || "default fonts"}`);
      }
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that site — try uploading an image instead");
    } finally { setSuggesting(false); }
  };

  const suggestThemeFromImage = async (files, schoolId) => {
    if (!files || !files.length) return toast.error("Pick at least one image");
    setSuggesting(true);
    try {
      const targetId = schoolId || schools[0]?.school_id;
      if (!targetId) return toast.error("Open a specific school's Theme dialog");
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/admin/schools/${targetId}/theme/suggest-from-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const palette = { primary: data.primary, accent: data.accent, dark: data.dark, light: data.light };
      const profile = {
        hero_image_url: data.hero_image_url,
        heading_font: null, body_font: null, font_links: [],
        vibe: data.vibe, tagline: null,
      };
      setPreviewPalette({ ...palette, _profile: profile });
      toast.success(`Extracted ${data.vibe} brand from ${files.length} image${files.length>1?"s":""}`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read those images");
    } finally { setSuggesting(false); }
  };

  const saveTheme = async (schoolId, palette) => {
    try {
      const { _profile, ...colors } = palette || {};
      await api.patch(`/admin/schools/${schoolId}`, {
        theme_palette: colors,
        brand_profile: _profile || null,
      });
      toast.success("Theme saved");
      refresh();
    } catch (e) { toast.error("Save failed"); }
  };

  const loadInstructors = async (schoolId) => {
    try {
      const { data } = await api.get(`/admin/schools/${schoolId}/instructors`);
      setInstructorsBySchool((s) => ({ ...s, [schoolId]: data }));
      return data;
    } catch (e) {
      toast.error("Couldn't load instructors");
      return [];
    }
  };

  const uploadInstructor = async (schoolId, form) => {
    if (!form.file) return toast.error("Photo or video required");
    if (!form.name?.trim()) return toast.error("Name required");
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("role", form.role || "Flight Instructor");
    fd.append("voice", form.voice || "onyx");
    fd.append("file", form.file);
    try {
      await api.post(`/admin/schools/${schoolId}/instructors`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Instructor added");
      await loadInstructors(schoolId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const setPrimaryFrame = async (schoolId, instructorId, framePath) => {
    try {
      await api.patch(`/admin/schools/${schoolId}/instructors/${instructorId}`, {
        primary_image_path: framePath,
      });
      toast.success("Primary frame updated");
      await loadInstructors(schoolId);
    } catch (e) { toast.error("Failed"); }
  };

  const deleteInstructor = async (schoolId, instructorId) => {
    if (!window.confirm("Delete this instructor?")) return;
    try {
      await api.delete(`/admin/schools/${schoolId}/instructors/${instructorId}`);
      toast.success("Removed");
      await loadInstructors(schoolId);
    } catch (e) { toast.error("Failed"); }
  };

  const startSchoolBatch = async (schoolId, lessonId, instructorId) => {
    if (!instructorId) return toast.error("Pick an instructor first");
    try {
      await api.post(`/admin/schools/${schoolId}/lessons/${lessonId}/avatar-studio/start`, { instructor_id: instructorId });
      toast.success(`Render kicked off for this school. Refresh in a few minutes to see videos.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const viewAs = async (schoolId, schoolName) => {
    try {
      await api.post(`/admin/view-as/${schoolId}`);
      toast.success(`Now viewing as ${schoolName}`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enter view-as");
    }
  };

  const startBatch = async (lessonId) => {
    setStarting(lessonId);
    try {
      await api.post(`/admin/lessons/${lessonId}/avatar-studio/start`);
      toast.success("Avatar generation kicked off");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setStarting(null); }
  };

  const syncFromStorage = async (lessonId) => {
    setStarting(lessonId);
    try {
      const { data } = await api.post(`/admin/lessons/${lessonId}/avatar-studio/sync-from-storage`);
      const parts = [];
      if (data.cover) parts.push("cover");
      if (data.backdrop) parts.push("backdrop");
      if (data.blocks_synced?.length) parts.push(`${data.blocks_synced.length} blocks`);
      toast.success(`Imported: ${parts.join(", ") || "nothing"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setStarting(null); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="super-admin-platform">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="hud-label text-red-600 flex items-center gap-2">
            <Crown className="w-3.5 h-3.5" /> PLATFORM.OVERVIEW
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl font-bold uppercase tracking-tight mt-2">Super Admin</h1>
          <p className="text-slate-600 mt-2 text-sm">Provision new flying schools, manage their members, and publish lesson content from the global Avatar Studio.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="create-school-btn">
              <Plus className="w-4 h-4 mr-2" /> New Flying School
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border border-slate-200 text-slate-900">
            <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Provision a Flying School</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="hud-label text-slate-600 mb-1.5 block">School Name</Label>
                <Input value={newSchool.name} onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })} className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="new-school-name" />
              </div>
              <div>
                <Label className="hud-label text-slate-600 mb-1.5 block">ICAO Code (optional)</Label>
                <Input value={newSchool.icao_code} onChange={(e) => setNewSchool({ ...newSchool, icao_code: e.target.value.toUpperCase() })} maxLength={4} className="bg-slate-50 border-slate-200 rounded-sm font-mono uppercase" data-testid="new-school-icao" />
              </div>
              <div>
                <Label className="hud-label text-slate-600 mb-1.5 block flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> School Admin Email (optional)
                </Label>
                <Input
                  type="email"
                  value={newSchool.admin_email}
                  onChange={(e) => setNewSchool({ ...newSchool, admin_email: e.target.value })}
                  placeholder="admin@school.com"
                  className="bg-slate-50 border-slate-200 rounded-sm font-mono"
                  data-testid="new-school-admin-email"
                />
                <div className="text-xs text-slate-500 mt-1.5 font-mono">
                  If set, this user becomes school_admin automatically on their next sign-in. If they don't exist yet, they're pre-bound — the assignment happens the moment they sign up.
                </div>
              </div>
              <div>
                <Label className="hud-label text-slate-600 mb-1.5 block">Their Website (optional)</Label>
                <Input
                  type="url"
                  value={newSchool.website_url}
                  onChange={(e) => setNewSchool({ ...newSchool, website_url: e.target.value })}
                  placeholder="https://flightschool.com"
                  className="bg-slate-50 border-slate-200 rounded-sm font-mono"
                  data-testid="new-school-website"
                />
                <div className="text-xs text-slate-500 mt-1.5 font-mono">
                  We'll crawl their site to pull brand colors — saved as the school's UI palette so their students see their branding.
                </div>
                {previewPalette && (
                  <div className="mt-3 flex items-center gap-3 p-3 instrument-panel">
                    <div className="hud-label text-[var(--brand-primary)] text-xs">PREVIEW</div>
                    <div className="w-8 h-8 rounded-sm border border-white/20" style={{ background: previewPalette.primary }} title={previewPalette.primary} />
                    <div className="w-8 h-8 rounded-sm border border-white/20" style={{ background: previewPalette.accent }} title={previewPalette.accent} />
                    <div className="w-8 h-8 rounded-sm border border-white/20" style={{ background: previewPalette.dark }} title={previewPalette.dark} />
                    <button onClick={() => setPreviewPalette(null)} className="text-slate-500 hover:text-red-600 text-xs ml-auto">clear</button>
                  </div>
                )}
              </div>
              <Button onClick={createSchool} disabled={creating} className="w-full bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="create-school-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* SCHOOLS */}
      <section className="mt-10">
        <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2"><Building2 className="w-4 h-4" /> FLYING.SCHOOLS · {schools.length}</div>
        {loading ? (
          <div className="text-slate-500 mt-4">Loading…</div>
        ) : schools.length === 0 ? (
          <div className="instrument-panel p-8 mt-4 text-center text-slate-500">No schools yet. Create the first one above.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {schools.map((s) => (
              <div key={s.school_id} className="instrument-panel p-5" data-testid={`school-card-${s.school_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-xl text-slate-900" style={{color:"#0F172A"}}>{s.name}</div>
                    {s.icao_code && <div className="hud-label text-[var(--brand-primary)] mt-1">{s.icao_code}</div>}
                  </div>
                  <span className="hud-label text-red-600">{s.plan?.toUpperCase()}</span>
                </div>
                <div className="mt-4 text-sm font-mono text-slate-600 space-y-1">
                  <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {s.member_count} member{s.member_count !== 1 ? "s" : ""}</div>
                  {s.pending_count > 0 && <div className="text-[var(--brand-primary)]">{s.pending_count} pending</div>}
                  <div className="flex items-center gap-2 text-xs">
                    CODE: <span className="text-[var(--brand-primary)]">{s.join_code}</span>
                    <button onClick={() => { navigator.clipboard.writeText(s.join_code); toast.success("Code copied"); }} className="text-slate-500 hover:text-[var(--brand-primary)]" data-testid={`copy-code-${s.school_id}`}>
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mt-2 break-all">{s.school_id}</div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => { await loadInstructors(s.school_id); setOpenSchool(s); }}
                    className="border-[var(--brand-primary)]/30 text-slate-700 hover:bg-[var(--brand-light)] font-heading tracking-wider rounded-sm text-xs px-2"
                    data-testid={`open-instructors-${s.school_id}`}
                  >
                    <UserCircle2 className="w-4 h-4 mr-1" /> Inst.
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => { await loadInstructors(s.school_id); setOpenSchoolStudio(s); }}
                    className="border-blue-200 text-[var(--brand-primary)] hover:bg-blue-50 font-heading tracking-wider rounded-sm text-xs px-2"
                    data-testid={`open-school-studio-${s.school_id}`}
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> Mod.
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setPreviewPalette(s.theme_palette || null); setOpenSchoolTheme(s); }}
                    className="border-red-200 text-red-700 hover:bg-red-50 font-heading tracking-wider rounded-sm text-xs px-2"
                    data-testid={`open-school-theme-${s.school_id}`}
                  >
                    <Palette className="w-4 h-4 mr-1" /> Theme
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => viewAs(s.school_id, s.name)}
                  className="mt-2 w-full border-blue-500/40 text-blue-300 hover:bg-blue-500/10 font-heading tracking-wider rounded-sm text-xs"
                  data-testid={`view-as-${s.school_id}`}
                >
                  <Eye className="w-4 h-4 mr-1.5" /> View as school admin
                </Button>
                {s.theme_palette && (
                  <div className="mt-3 flex items-center gap-1.5" data-testid={`theme-preview-${s.school_id}`}>
                    <span className="hud-label text-xs text-slate-500">BRAND</span>
                    <div className="w-4 h-4 rounded-sm border border-white/20" style={{ background: s.theme_palette.primary }} />
                    <div className="w-4 h-4 rounded-sm border border-white/20" style={{ background: s.theme_palette.accent }} />
                    {s.theme_palette.dark && <div className="w-4 h-4 rounded-sm border border-white/20" style={{ background: s.theme_palette.dark }} />}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* AVATAR STUDIO */}
      <section className="mt-12">
        <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2"><Sparkles className="w-4 h-4" /> AVATAR.STUDIO · GLOBAL</div>
        <p className="text-slate-600 text-sm mt-2">
          Generate cinematic talking-head avatar videos for every narration block. Lesson content is shared across all schools — generate once, all tenants benefit.
          Use "Sync from Storage" to pull pre-rendered videos from shared object storage without paying for re-generation.
        </p>

        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          {lessons.map((l) => {
            const s = avStatus[l.id];
            const isRunning = s?.job?.status === "running" || s?.job?.status === "queued";
            return (
              <div key={l.id} className="instrument-panel p-5" data-testid={`global-avatar-${l.id}`}>
                <div className="font-heading text-lg" style={{color:"#0F172A"}}>{l.title}</div>
                <div className="text-slate-500 text-sm font-mono mt-1">{l.id}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="instrument-panel p-2">
                    <div className="hud-label text-green-400">READY</div>
                    <div className="font-heading text-2xl mt-1" style={{color:"#0F172A"}}>{s?.ready?.length ?? "—"}</div>
                  </div>
                  <div className="instrument-panel p-2">
                    <div className="hud-label text-[var(--brand-primary)]">PENDING</div>
                    <div className="font-heading text-2xl mt-1" style={{color:"#0F172A"}}>{s?.pending?.length ?? "—"}</div>
                  </div>
                  <div className="instrument-panel p-2">
                    <div className="hud-label text-red-600">FAILED</div>
                    <div className="font-heading text-2xl mt-1" style={{color:"#0F172A"}}>{s?.failed?.length ?? "—"}</div>
                  </div>
                </div>
                {isRunning && s?.job && (
                  <div className="mt-3 text-xs font-mono text-[var(--brand-primary)]">
                    Job: {s.job.done}/{s.job.total} blocks
                  </div>
                )}
                <div className="mt-4 space-y-2">
                  <Button
                    onClick={() => startBatch(l.id)}
                    disabled={!!starting || isRunning}
                    className="w-full bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm"
                    data-testid={`global-gen-${l.id}`}
                  >
                    {isRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> :
                     starting === l.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> :
                     <><PlayCircle className="w-4 h-4 mr-2" /> {s?.ready?.length ? "Re-Generate" : "Generate"}</>}
                  </Button>
                  <Button
                    onClick={() => syncFromStorage(l.id)}
                    disabled={!!starting || isRunning}
                    variant="outline"
                    className="w-full border-blue-200 text-[var(--brand-primary)] hover:bg-blue-50 font-heading tracking-wider rounded-sm"
                    data-testid={`global-sync-${l.id}`}
                  >
                    ↡ Sync from Storage
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="mt-12 instrument-panel p-5">
        <div className="hud-label text-slate-600">ABOUT.LESSON.CONTENT</div>
        <p className="text-slate-600 text-sm mt-2">
          Lessons, characters, and the default Captain avatar videos are <span className="text-[var(--brand-primary)]">global</span> — shared across every flying school as the fallback presenter.
          When you add school-specific instructors and render modules under "Modules", students at THAT school see their own faces; everyone else falls back to the global Captain.
        </p>
        <Link to="/dashboard" className="text-[var(--brand-primary)] hover:text-[var(--brand-primary)] text-sm mt-3 inline-block">← Back to Hangar</Link>
      </div>

      {openSchool && (
        <InstructorManagerDialog
          school={openSchool}
          instructors={instructorsBySchool[openSchool.school_id] || []}
          onClose={() => setOpenSchool(null)}
          onUpload={(form) => uploadInstructor(openSchool.school_id, form)}
          onSetPrimary={(iid, framePath) => setPrimaryFrame(openSchool.school_id, iid, framePath)}
          onDelete={(iid) => deleteInstructor(openSchool.school_id, iid)}
        />
      )}
      {openSchoolStudio && (
        <SchoolStudioDialog
          school={openSchoolStudio}
          lessons={lessons}
          instructors={instructorsBySchool[openSchoolStudio.school_id] || []}
          onClose={() => setOpenSchoolStudio(null)}
          onStart={(lessonId, instructorId) => startSchoolBatch(openSchoolStudio.school_id, lessonId, instructorId)}
        />
      )}
      {openSchoolTheme && (
        <ThemeEditorDialog
          school={openSchoolTheme}
          onClose={() => { setOpenSchoolTheme(null); setPreviewPalette(null); }}
          onSuggest={(url) => suggestTheme(url, openSchoolTheme.school_id)}
          onSuggestFromImage={(file) => suggestThemeFromImage(file, openSchoolTheme.school_id)}
          previewPalette={previewPalette}
          setPreviewPalette={setPreviewPalette}
          suggesting={suggesting}
          onSave={async (palette) => { await saveTheme(openSchoolTheme.school_id, palette); setOpenSchoolTheme(null); }}
        />
      )}
    </div>
  );
}


function InstructorManagerDialog({ school, instructors, onClose, onUpload, onSetPrimary, onDelete }) {
  const [form, setForm] = useState({ name: "", role: "Flight Instructor", voice: "onyx", file: null });
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    setUploading(true);
    await onUpload(form);
    setUploading(false);
    setForm({ name: "", role: "Flight Instructor", voice: "onyx", file: null });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Instructors · {school.name}</DialogTitle></DialogHeader>

        <div className="instrument-panel p-4 space-y-3" data-testid="instructor-upload-form">
          <div className="hud-label text-[var(--brand-primary)]">ADD.INSTRUCTOR</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="hud-label text-slate-600 mb-1.5 block">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="instructor-name" />
            </div>
            <div>
              <Label className="hud-label text-slate-600 mb-1.5 block">Role</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="instructor-role" />
            </div>
            <div>
              <Label className="hud-label text-slate-600 mb-1.5 block">Voice</Label>
              <Select value={form.voice} onValueChange={(v) => setForm({ ...form, voice: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="instructor-voice">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-50 border-slate-200 text-slate-900 rounded-sm">
                  <SelectItem value="onyx">Onyx (male, deep)</SelectItem>
                  <SelectItem value="alloy">Alloy (neutral)</SelectItem>
                  <SelectItem value="echo">Echo (male)</SelectItem>
                  <SelectItem value="fable">Fable (warm)</SelectItem>
                  <SelectItem value="nova">Nova (female)</SelectItem>
                  <SelectItem value="shimmer">Shimmer (female, bright)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="hud-label text-slate-600 mb-1.5 block">Photo or short video</Label>
              <Input type="file" accept="image/*,video/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="bg-slate-50 border-slate-200 rounded-sm font-mono text-xs" data-testid="instructor-file" />
              <div className="text-xs text-slate-500 mt-1.5">If video, 3 frames will be auto-extracted; you can pick which one to use.</div>
            </div>
          </div>
          <Button onClick={submit} disabled={uploading} className="w-full bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="instructor-upload-submit">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Add Instructor</>}
          </Button>
        </div>

        <div className="space-y-3 mt-4">
          <div className="hud-label text-[var(--brand-primary)]">ROSTER · {instructors.length}</div>
          {instructors.length === 0 && <div className="text-slate-500 text-sm">No instructors yet — add one above.</div>}
          {instructors.map((ins) => (
            <div key={ins.instructor_id} className="instrument-panel p-4" data-testid={`instructor-${ins.instructor_id}`}>
              <div className="flex items-start gap-4">
                <img src={ins.image_url} alt={ins.name} className="w-20 h-20 object-cover border border-[var(--brand-primary)]/30 rounded-sm" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg" style={{color:"#0F172A"}}>{ins.name}</div>
                  <div className="text-slate-600 text-sm">{ins.role} · voice: {ins.voice} · {ins.media_type}</div>
                </div>
                <button onClick={() => onDelete(ins.instructor_id)} className="text-red-600 hover:text-red-700" data-testid={`delete-instructor-${ins.instructor_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {ins.frames && ins.frames.length > 1 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {ins.frames.map((fr, i) => {
                    const isPrimary = ins.primary_image_path === fr.path;
                    return (
                      <button
                        key={i}
                        onClick={() => onSetPrimary(ins.instructor_id, fr.path)}
                        className={`relative border-2 rounded-sm overflow-hidden ${isPrimary ? "border-amber-500" : "border-slate-200 hover:border-zinc-500"}`}
                        data-testid={`frame-${ins.instructor_id}-${i}`}
                      >
                        <img src={`/api/files/${fr.path}`} alt={fr.label} className="w-full h-24 object-cover" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 hud-label text-center py-0.5 text-[10px]">
                          {isPrimary ? "PRIMARY" : fr.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SchoolStudioDialog({ school, lessons, instructors, onClose, onStart }) {
  const [pick, setPick] = useState({}); // lesson_id -> instructor_id

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Modules · {school.name}</DialogTitle></DialogHeader>
        <p className="text-slate-600 text-sm">
          Pick which instructor delivers each lesson for THIS school's students. Renders go to a school-scoped storage path; students at other schools keep seeing the global Captain.
        </p>
        {instructors.length === 0 && (
          <div className="instrument-panel p-4 border-slate-200">
            <div className="hud-label text-[var(--brand-primary)]">NO INSTRUCTORS</div>
            <p className="text-slate-600 text-sm mt-1">Add at least one instructor under "Instructors" first.</p>
          </div>
        )}
        {lessons.map((l) => {
          const chosen = pick[l.id];
          return (
            <div key={l.id} className="instrument-panel p-4 space-y-3" data-testid={`school-studio-lesson-${l.id}`}>
              <div className="font-heading text-lg" style={{color:"#0F172A"}}>{l.title}</div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <Label className="hud-label text-slate-600 mb-1.5 block">Instructor</Label>
                  <Select value={chosen || ""} onValueChange={(v) => setPick({ ...pick, [l.id]: v })}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm" data-testid={`pick-instructor-${l.id}`}>
                      <SelectValue placeholder="— select —" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-50 border-slate-200 text-slate-900 rounded-sm">
                      {instructors.map((ins) => (
                        <SelectItem key={ins.instructor_id} value={ins.instructor_id}>{ins.name} · {ins.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => onStart(l.id, chosen)}
                  disabled={!chosen}
                  className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm"
                  data-testid={`start-school-render-${l.id}`}
                >
                  <PlayCircle className="w-4 h-4 mr-1.5" /> Render
                </Button>
              </div>
            </div>
          );
        })}
      </DialogContent>
    </Dialog>
  );
}


function ThemeEditorDialog({ school, onClose, onSuggest, onSuggestFromImage, previewPalette, setPreviewPalette, suggesting, onSave }) {
  const initial = previewPalette || school.theme_palette || { primary: "#F5A623", accent: "#FF2A6D", dark: "#0B0716" };
  const [url, setUrl] = useState(school.website_url || "");
  const [palette, setPalette] = useState(initial);

  useEffect(() => {
    if (previewPalette) setPalette(previewPalette);
  }, [previewPalette]);

  // Lazy import without circular dep
  const tryLive = () => {
    import("../lib/auth").then((m) => {
      const { _profile, ...colors } = palette;
      m.applyTheme(colors, _profile || school.brand_profile);
    });
  };
  const revert = () => {
    import("../lib/auth").then((m) => m.applyTheme(school.theme_palette, school.brand_profile));
  };

  const update = (key, value) => {
    const next = { ...palette, [key]: value };
    setPalette(next);
    setPreviewPalette(next);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">Brand Theme · {school.name}</DialogTitle></DialogHeader>
        <p className="text-slate-600 text-sm">
          Apply this school's brand colors to their students' view. We'll crawl their website to suggest a palette — you can fine-tune it before saving. The Aviatron layout and structure stay the same.
        </p>

        <div className="instrument-panel p-4 space-y-3">
          <Label className="hud-label text-slate-600 flex items-center gap-1.5"><Globe className="w-3 h-3" /> WEBSITE</Label>
          <div className="flex gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://flightschool.com" className="bg-slate-50 border-slate-200 rounded-sm font-mono flex-1" data-testid="theme-website-input" />
            <Button onClick={() => onSuggest(url)} disabled={suggesting || !url} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="theme-suggest-btn">
              {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suggest"}
            </Button>
          </div>
          <div className="border-t border-slate-200 pt-3">
            <Label className="hud-label text-slate-600 flex items-center gap-1.5"><Upload className="w-3 h-3" /> OR UPLOAD IMAGES</Label>
            <div className="text-xs text-slate-500 mt-1 mb-2">
              Bot-walled site? Upload one or more screenshots (homepage, gallery, brochure pages), logos, or brand images. Hold Ctrl/Cmd to pick multiple — we'll pool the palettes across all of them and use the largest as the hero backdrop.
            </div>
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => { const fs = e.target.files; if (fs && fs.length) onSuggestFromImage(fs); e.target.value = ""; }}
              disabled={suggesting}
              className="bg-slate-50 border-slate-200 rounded-sm font-mono text-xs"
              data-testid="theme-image-upload"
            />
          </div>
        </div>

        <div className="space-y-3">
          {["primary", "accent", "dark"].map((k) => (
            <div key={k} className="instrument-panel p-3 flex items-center gap-3">
              <div className="hud-label text-slate-600 w-24 uppercase">{k}</div>
              <input
                type="color"
                value={palette[k] || "#000000"}
                onChange={(e) => update(k, e.target.value.toUpperCase())}
                className="w-14 h-10 rounded-sm cursor-pointer bg-transparent"
                data-testid={`color-${k}`}
              />
              <Input
                value={palette[k] || ""}
                onChange={(e) => update(k, e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-sm font-mono uppercase w-32"
              />
            </div>
          ))}
        </div>

        <div className="instrument-panel p-4">
          <div className="hud-label text-slate-600 mb-2">LIVE PREVIEW</div>
          <div className="rounded-sm overflow-hidden border border-white/10" style={{ background: palette.dark || "#0A1128" }}>
            <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${palette.accent}, ${palette.primary}, ${palette.accent})` }} />
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 border" style={{ borderColor: palette.primary, background: palette.dark }} />
              <div>
                <div className="font-heading text-base tracking-tight font-bold" style={{ color: palette.primary }}>Aviatron</div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: palette.accent }}>Flight Training · {school.name}</div>
              </div>
              <button className="ml-auto px-4 py-1.5 text-sm font-heading tracking-wider rounded-sm text-black" style={{ background: palette.primary }}>
                Start Lesson
              </button>
            </div>
          </div>
        </div>

        <Button onClick={() => onSave(palette)} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="theme-save-btn">
          Save Theme
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={tryLive} variant="outline" className="border-blue-200 text-[var(--brand-primary)] hover:bg-blue-50 rounded-sm" data-testid="theme-try-live">
            Try Live (preview)
          </Button>
          <Button onClick={() => { revert(); onClose(); }} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-sm" data-testid="theme-revert">
            Cancel & Revert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

